<template>
  <div class="am3-activity-item w-full">
    <!-- Pending row: activity known to be coming but not yet visible from the server -->
    <div
      v-if="pending"
      class="flex text-left w-full max-w-full overflow-x-hidden min-h-10 items-center px-4 py-2"
      data-test-id="am3-activity-item-pending"
    >
      <div class="flex-shrink-0 mr-4">
        <div class="w-9 h-9 rounded-full overflow-hidden">
          <AM3LoadingContent
            shape="square"
            size="md"
          />
        </div>
      </div>
      <div class="flex-1 min-w-0">
        <AM3LoadingContent
          shape="text"
          size="md"
        />
      </div>
    </div>

    <!-- Activity row -->
    <div
      v-else
      v-tooltip.bottom="activityTooltip.length >= 80 ? activityTooltip : ''"
      class="flex text-left w-full max-w-full overflow-x-hidden outline-brand min-h-10 items-center px-4 py-2 hover:bg-brand-light "
      data-test-id="am3-activity-item"
    >
      <!-- Icon -->
      <div class="flex-shrink-0 mr-4">
        <div
          class="w-9 h-9 flex items-center justify-center rounded-full outline outline-1 outline-surface-light overflow-hidden"
          :class="computeActivityIcon(activity).classes"
          :title="activity?.activityType ? activity?.activityType : 'Unknown activity icon' "
        >
          <img
            v-if="getActivityImage(activity)"
            v-tooltip.bottom="activityTooltip"
            :src="getActivityImage(activity)"
            :alt="activity.activityType"
            class="w-full h-full object-cover rounded-full"
            width="36"
            height="36"
          >
          <!-- Default icon fallback -->
          <AM3Icon
            v-else
            :type="computeActivityIcon(activity).type"
            size="sm"
          />
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0 flex flex-row overflow-hidden items-baseline overflow-x-hidden">
        <p
          class="activity-item-title min-w-0 flex-grow text-sm line-clamp-1 text-primary [&_>_a]:font-bold [&_>_a:hover]:underline [&_>_a]:inline-block  &_>_strong]:font-bold"
          v-html="activityTitle"
        />
        <span class="text-xs flex-grow-0 basis-20 text-right text-secondary whitespace-nowrap ml-auto mr-0">
          <AM3Formatter
            :value="activity?.dateTime"
            type="relative-time"
          />
        </span>
      </div>
    </div>

    <!-- Additional details, always shown (no collapse) -->
    <div
      v-if="!pending && canExpand"
      :data-type="activity.activityType"
    >
      <div class="pr-8 py-4 ml-12 mx-4 md:ml-[4.25rem] mb-4 rounded-lg">
        <!-- message stats:  -->
        <transition-group
          v-bind="fadeSlideTransition"
          tag="div"
        >
          <div
            v-if="activity.activityType === 'fan-message'"
            key="fan-message-stats"
            class="mb-2"
          >
            <div
              v-if="activity.additionalInfo.conversions?.totalRevenueCents || activity.additionalInfo.messageBody"
              class="flex flex-wrap items-center gap-4 bg-surfaces-neutral py-1 px-2 rounded-lg"
            >
              <span
                v-if="activity.additionalInfo.conversions?.totalRevenueCents"
                class="flex items-center gap-1"
              >
                <span>Ticket sales conversions:</span>
                <AM3Formatter
                  class="text-success font-bold"
                  type="currency"
                  :value="activity.additionalInfo.conversions?.totalRevenueCents ?? 0"
                />
                <AM3Icon
                  v-tooltip.bottom="'Ticket sales conversion based on 14 day attribution window'"
                  class="cursor-help"
                  type="information-circle"
                  size="md"
                />
              </span>

              <span
                v-if="activity.additionalInfo.messageBody"
                class="flex items-center gap-1"
              >
                <span>Subject:</span>
                <span>{{ activity.additionalInfo.messageBody }}</span>
              </span>
            </div>

            <!-- short url clicks within a message -->
            <AM3ActivityShortUrlClicks
              :short-url-clicks="activity.additionalInfo.shortUrlClicks"
              :last-open-time="fanMessageLastOpenTime(activity.additionalInfo)"
              :last-click-time="fanMessageLastClickTime(activity.additionalInfo)"
              :sent-time="activity.activityTime.toString()"
              :status="activity.additionalInfo.status"
              :provider="activity.additionalInfo.provider"
            />
          </div>

          <!-- subscription stats:  -->
          <div
            v-if="activity.activityType === 'subscription'"
            key="subscription-stats"
            class="bg-surfaces-neutral py-2 px-4 rounded-lg inline-flex flex-row space-x-2 mb-2"
          >
            <div>
              Email Address: {{ activity.fanPromoterAccount?.emailAddress }}
            </div>
            <div>
              Fan ID: {{ activity.fanPromoterAccount?.oid }}
            </div>
          </div>

          <!-- purchase stats:  -->
          <div
            v-if="activity.activityType === 'event-purchase'"
            key="purchase-stats"
            class="bg-surfaces-neutral py-2 px-4 rounded-lg inline-flex flex-row space-x-2 mb-2"
          >
            <div v-if="activity.additionalInfo.quantity">
              Quantity: {{ activity.additionalInfo.quantity || '0' }}
            </div>
            <div v-if="activity.additionalInfo.totalCost">
              Total Amount: <AM3Formatter
                type="currency"
                :value="activity.additionalInfo.totalCost"
                :options="{ code: activity.additionalInfo.currencyCode }"
              />
            </div>
          </div>

          <!-- attendees  -->
          <div
            v-if="activity.activityType === 'event-purchase'"
            key="attendees"
            class=" py-2 px-4 rounded-lg block flex-col space-y-2 mb-2"
          >
            <div v-if="activity.additionalInfo.attendees">
              <h2 class="mb-2">
                Event Attendees:
              </h2>
              <ul>
                <li
                  v-for="attendee in activity.additionalInfo.attendees"
                  :key="attendee.fanOid"
                  class="ml-2"
                >
                  {{ attendee.firstName }} {{ attendee.lastName }}
                </li>
              </ul>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import AM3ActivityShortUrlClicks from './AM3ActivityShortUrlClicks.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Formatter from '@/components/am3-generic/data/AM3Formatter.vue'
import AM3LoadingContent from '@/components/am3-generic/base/AM3LoadingContent.vue'
import { computeActivityIcon } from '@/utils/computeActivityIcon'
import { computeActivityTitle } from '@/utils/computeActivityTitle'
import { computeActivityTooltip } from '@/utils/computeActivityTooltip'
import type {
  Activity,
  ActivityFanMessage,
} from '@/types/resources/activity.ts'
import type { SimpleFan } from '@/types/resources/fan'
import { useCurrencySymbol } from '@/composables/useCurrencySymbol'

const router = useRouter()

// Component receives complete server activity object with all data including additionalInfo.
// Not required when `pending` is true - the pending row never reads from it.
const props = defineProps({
  activity: {
    type: Object as () => Activity,
    default: () => ({} as Activity),
  },
  customer: {
    type: Object as () => SimpleFan | null,
    default: null,
  },
  // Renders a loading placeholder in place of the activity content - used when we know a new
  // activity row is coming (e.g. a just-saved subscription edit) but it hasn't shown up yet.
  pending: {
    type: Boolean,
    default: false,
  },
})

// Transition classes for fade-slide animation
const fadeSlideTransition = {
  name: 'fade-slide',
  enterActiveClass: 'transition-all duration-300 ease-out',
  leaveActiveClass: 'transition-all duration-200 ease-in',
  enterFromClass: 'opacity-0 transform -translate-y-2',
  enterToClass: 'opacity-100 transform translate-y-0',
  leaveFromClass: 'opacity-100 transform translate-y-0',
  leaveToClass: 'opacity-0 transform -translate-y-2',
}

const currencySymbol = useCurrencySymbol()

const activityTitle = computed(() => computeActivityTitle(props.activity, props.customer, currencySymbol.value))
const activityTooltip = computed(() => computeActivityTooltip(props.activity, currencySymbol.value))

/**
 * Determine if an activity type can be expanded
 */
const canExpand = computed(() => {
  if (!props.activity) return false

  // Based on MD file, these are the activity types that can expand
  const expandableTypes = [
    'fan-message', // Shows message details, opens/clicks
    // 'subscription', // Shows subscription details
    // 'data-capture', // Shows capture details
    // 'link-activity', // Shows link details
    // 'short-link', // Shows link details
    // 'website-activity', // Shows website visit details
    // 'event-purchase', // Shows purchase details
    // 'ecommerce-purchase', // Shows purchase details
    // 'automation', // Shows automation details
    // 'profile-details', // Shows profile update details
  ]

  return expandableTypes.includes(props.activity.activityType)
})

/**
 * Get image URL for activity if available
 */
function getActivityImage(activity: Activity): string | undefined {
  if (!activity) return undefined

  switch (activity.activityType) {
    case 'event-attendance':
      // TypeScript knows this is ActivityEventAttendance
      return activity.additionalInfo.eventImageUrl || undefined

    case 'event-purchase':
      // TypeScript knows this is ActivityEventPurchase
      const eventResources = activity?.additionalInfo?.resources
      if (eventResources && Array.isArray(eventResources)) {
        const eventImageResource = eventResources.find(resource => resource.assetType === 'event-image')
        return eventImageResource?.url || undefined
      }
      return undefined

    case 'campaign-registration':
      console.log(activity)
      // TypeScript knows this is ActivityCampaignRegistration
      return activity?.additionalInfo?.resources?.[0]?.url || undefined

    case 'ecommerce-purchase':
      // TypeScript knows this is ActivityPurchase
      const ecommerceResources = activity.additionalInfo.resources
      if (ecommerceResources && Array.isArray(ecommerceResources)) {
        const eventImageResource = ecommerceResources.find(resource => resource.assetType === 'event-image')
        return eventImageResource?.url || undefined
      }
      return undefined

    default:
      return undefined
  }
}

function fanMessageLastOpenTime(stats: ActivityFanMessage['additionalInfo']): string | undefined {
  const entry = stats.actions?.find(item => item.action === 'last-open')
  return entry?.ts ? new Date(Number(entry.ts) * 1000).toISOString() : undefined
}

function fanMessageLastClickTime(stats: ActivityFanMessage['additionalInfo']): string | undefined {
  const entry = stats.actions?.find(item => item.action === 'last-click')
  return entry?.ts ? new Date(Number(entry.ts) * 1000).toISOString() : undefined
}
</script>
